import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { products } from '../../data/products';
import { ProductCard } from '../products/ProductCard';
import { useTranslation } from '../../lib/i18n';
interface ProductShowcaseProps {
  onRequestQuote: (productId: string) => void;
}
export function ProductShowcase({
  onRequestQuote
}: ProductShowcaseProps) {
  const {
    t,
    lang
  } = useTranslation();
  const navigate = useNavigate();
  const showcaseProducts = [products.find(p => p.id === 'sca'), products.find(p => p.id === 'standard'), products.find(p => p.id === 'raptor'), products.find(p => p.id === 'scc-pro')].filter(Boolean) as typeof products;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleScroll = () => {
    if (!scrollRef.current || !scrollRef.current.children[0]) return;
    const scrollLeft = scrollRef.current.scrollLeft;
    const itemWidth = scrollRef.current.children[0].clientWidth;
    const newIndex = Math.round(scrollLeft / itemWidth);
    setActiveIndex(Math.min(newIndex, showcaseProducts.length - 1));
  };
  const scrollTo = (index: number) => {
    if (!scrollRef.current || !scrollRef.current.children[0]) return;
    const itemWidth = scrollRef.current.children[0].clientWidth;
    scrollRef.current.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth'
    });
  };
  const prev = () => {
    if (activeIndex === 0) {
      scrollTo(showcaseProducts.length - 1);
    } else {
      scrollTo(activeIndex - 1);
    }
  };
  const next = () => {
    if (!scrollRef.current) return;
    const isAtEnd = Math.ceil(scrollRef.current.scrollLeft + scrollRef.current.clientWidth) >= scrollRef.current.scrollWidth;
    if (isAtEnd || activeIndex === showcaseProducts.length - 1) {
      scrollTo(0);
    } else {
      scrollTo(activeIndex + 1);
    }
  };
  return <section id="products" className="py-12 md:py-20 bg-dark-bg" data-id="element-260" data-mp="3u7m10ajr7f7o">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-261" data-mp="3u5rcynd6r6zd">
        <div className="text-center mb-8 md:mb-12" data-id="element-262" data-mp="3palu5xf2y8fi">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-brand-yellow mb-3 md:mb-4" data-id="element-263" data-mp="6ut5hms8r8m4">
            {t.showcase.titleHighlight}
          </h2>
        </div>

        <div className="relative group" data-id="element-264" data-mp="228dmqswb5l8">
          {/* Left arrow */}
          <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 sm:-translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface/90 border border-brand-yellow/40 sm:border-dark-border text-brand-yellow sm:text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 md:opacity-100 focus:opacity-100" aria-label="Prethodni" data-id="element-265" data-mp="grxvxyt4dq8q">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-266" data-mp="2nli2okm7dxte" />
          </button>

          {/* Sliding track */}
          <div ref={scrollRef} onScroll={handleScroll} className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-267" data-mp="2dgjsj05mgckb">
            {showcaseProducts.map(product => <div key={product.id} className="flex-shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-center sm:snap-start" data-id="element-268" data-mp="28o057bh4u32b">
                <ProductCard product={product} layout="vertical" onRequestQuote={onRequestQuote} onSelect={id => navigate(`/products?model=${id}`)} data-id="element-269" data-mp="1yr461wd4itaf" />
              </div>)}
          </div>

          {/* Right arrow */}
          <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 sm:translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface/90 border border-brand-yellow/40 sm:border-dark-border text-brand-yellow sm:text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 md:opacity-100 focus:opacity-100" aria-label="Sledeći" data-id="element-270" data-mp="3f6f7f0hxzelm">
            <ChevronRightIcon className="w-5 h-5" data-id="element-271" data-mp="161mtsqjecvfd" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-4 sm:hidden" data-id="element-272" data-mp="1156780ruip4e">
          {showcaseProducts.map((_, idx) => <button key={idx} onClick={() => scrollTo(idx)} className={`transition-all rounded-full ${idx === activeIndex ? 'w-6 h-1.5 bg-brand-yellow' : 'w-1.5 h-1.5 bg-gray-700 hover:bg-gray-500'}`} aria-label={`Strana ${idx + 1}`} data-id="element-273" data-mp="wajxe21ex4mf" data-mpi={idx} />)}
        </div>

        <div className="mt-10 text-center" data-id="element-274" data-mp="dcz7qsqtq20r">
          <Link to="/products" className="inline-flex items-center gap-2 text-brand-yellow hover:text-white transition-colors font-semibold text-sm border border-brand-yellow/30 hover:border-brand-yellow/60 rounded-lg px-5 py-3" data-id="element-275" data-mp="deai5bery80u">
            {lang === 'en' ? 'View all models' : lang === 'ru' ? 'Посмотреть все модели' : 'Pogledajte sve modele'}
            <ArrowRightIcon className="w-4 h-4" data-id="element-276" data-mp="26ami2v1d001l" />
          </Link>
        </div>
      </div>
    </section>;
}